import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  cancelBooking,
  getBookingShare,
  revokeBookingShare,
  shortenBookingTimes,
  rescheduleBooking,
  ApiError,
} from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import type { AdminShareLink, Booking } from '../../lib/types';
import { POLICY, POLICY_MIN_TIME } from '../../lib/policy';
import { TimeSelect } from '../../components/TimeSelect';
import { PS, T } from '../../lib/theme';
import {
  formatTimeOfDay,
  formatClock,
  formatLocal,
  formatVenueTime,
  minutesToHm,
} from '../../lib/time';
import { fullName } from '../../lib/format';

/** Minutes since midnight for an "HH:MM" (or "HH:MM:SS") string. */
function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}

/** Turn a reschedule failure into a staff-facing message, surfacing the
 *  structured 409 conflict detail (occupant + next free time) when present. */
function rescheduleErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Reschedule failed';
  if (err.status === 409) {
    const d = err.detail as
      | { conflicts?: { owner_name?: string | null }[]; next_free_time?: string | null }
      | undefined;
    const who = d?.conflicts
      ?.map((c) => c.owner_name)
      .filter((n): n is string => Boolean(n))
      .join(', ');
    const nextFree = d?.next_free_time ? ` Next free at ${formatClock(d.next_free_time)}.` : '';
    return `Slot is already taken${who ? ` by ${who}` : ''}.${nextFree}`;
  }
  return err.message;
}

function formatTimestamp(value: string): string {
  return `${formatLocal(value, 'yyyy-MM-dd')} ${formatVenueTime(value)}`;
}

interface Props {
  open: boolean;
  booking: Booking | null;
  onClose: () => void;
  /** Called after a successful action so the page can refetch. */
  onChanged: () => void;
}

export function BookingDetailModal({ open, booking, onClose, onChanged }: Props) {
  const { push } = useToast();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [shareLink, setShareLink] = useState<AdminShareLink | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [optimisticRevokedAt, setOptimisticRevokedAt] = useState<string | null>(null);
  const shareSessionRef = useRef(0);

  useLayoutEffect(() => {
    shareSessionRef.current += 1;
    return () => {
      shareSessionRef.current += 1;
    };
  }, [booking?.id, open]);

  // Default the editable times to the booking's current values whenever the
  // modal opens for a fresh booking. Intentionally keyed on the id, not the
  // whole `booking` object, so a parent re-render with the same booking
  // doesn't stomp on times the user is actively editing. This seeding runs for
  // *every* booking (it is not gated on payment_type), so `newStart`/`newEnd`
  // are always pre-filled with the current times. NOTE: this state is shared by
  // two mutually-exclusive editors — the coach-block "Change times" panel (uses
  // both newStart+newEnd) and the regular-booking "Reschedule" panel (uses
  // newStart only). Only one is rendered for a given booking, so they never
  // fight; keep it that way.
  useEffect(() => {
    if (booking) {
      setNewStart(formatTimeOfDay(booking.start_time));
      setNewEnd(formatTimeOfDay(booking.end_time));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  useEffect(() => {
    let current = true;
    setShareLink(null);
    setShareError(null);
    setConfirmRevokeOpen(false);
    setOptimisticRevokedAt(null);
    setRevoking(false);

    if (!open || !booking?.share_enabled_at || booking.share_revoked_at) {
      setShareLoading(false);
      return () => {
        current = false;
      };
    }

    setShareLoading(true);
    void getBookingShare(booking.id)
      .then((link) => {
        if (current) setShareLink(link);
      })
      .catch((err: unknown) => {
        if (!current) return;
        setShareError(err instanceof ApiError ? err.message : 'Could not load the share link.');
      })
      .finally(() => {
        if (current) setShareLoading(false);
      });

    return () => {
      current = false;
    };
  }, [booking?.id, booking?.share_enabled_at, booking?.share_revoked_at, open]);

  if (!booking) return null;

  const handleCancel = async () => {
    try {
      await cancelBooking(booking.id);
      push('success', `Booking #${booking.id} cancelled.`);
      setConfirmCancelOpen(false);
      onClose();
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Cancel failed';
      push('error', msg);
    }
  };

  const timesChanged =
    newStart !== formatTimeOfDay(booking.start_time) ||
    newEnd !== formatTimeOfDay(booking.end_time);

  const handleUpdateTimes = async () => {
    if (!newStart || !newEnd) {
      push('error', 'Pick both a start and an end time.');
      return;
    }
    if (!timesChanged) return;
    setUpdating(true);
    try {
      await shortenBookingTimes(booking.id, newStart, newEnd);
      push('success', `Booking #${booking.id} times updated to ${formatClock(newStart)}–${formatClock(newEnd)}.`);
      onClose();
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Update failed';
      push('error', msg);
    } finally {
      setUpdating(false);
    }
  };

  // Reschedule (regular bookings): move to a new start on the same day, same
  // table. Duration is preserved, so the end time is derived for display; the
  // API computes the authoritative end.
  //
  // Compare by minute-of-day, not raw string, so the dirty-check can't be fooled
  // by any format drift between TimeSelect's canonical "HH:MM" and
  // formatTimeOfDay's output (a mismatch there would leave Apply permanently
  // enabled and allow submitting a no-op reschedule).
  const rescheduleChanged =
    !!newStart && hmToMinutes(newStart) !== hmToMinutes(formatTimeOfDay(booking.start_time));
  // Same-day bookings can end no later than the API's 24:00 boundary, so the
  // latest valid start is 24:00 − duration. Cap the selector there (impossible
  // starts are never offered) and, defensively, hide the derived end if a start
  // + duration would still overflow past midnight — showing "24:45" is worse
  // than showing nothing.
  const rescheduleMaxStart = minutesToHm(Math.max(0, 1440 - booking.duration_minutes));
  const derivedEndMinutes = newStart ? hmToMinutes(newStart) + booking.duration_minutes : null;
  const derivedEnd =
    derivedEndMinutes !== null && derivedEndMinutes <= 1440
      ? minutesToHm(derivedEndMinutes)
      : '';

  const handleReschedule = async () => {
    if (!newStart) {
      push('error', 'Pick a new start time.');
      return;
    }
    if (!rescheduleChanged) return;
    setUpdating(true);
    try {
      const { booking: updated } = await rescheduleBooking(
        booking.id,
        newStart,
        booking.table_id,
      );
      push(
        'success',
        `Booking #${booking.id} moved to ${formatClock(updated.start_time)}–${formatClock(updated.end_time)}.`,
      );
      onClose();
      onChanged();
    } catch (err) {
      push('error', rescheduleErrorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareLink?.url) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      push('success', 'Share link copied.');
    } catch {
      push('error', 'Could not copy the share link.');
    }
  };

  const handleRevokeShareLink = async () => {
    const shareSession = shareSessionRef.current;
    setRevoking(true);
    try {
      await revokeBookingShare(booking.id);
      if (shareSessionRef.current !== shareSession) return;
      const revokedAt = new Date().toISOString();
      setOptimisticRevokedAt(revokedAt);
      setShareLink((current) => current ? { ...current, share_revoked_at: revokedAt } : current);
      setConfirmRevokeOpen(false);
      push('success', 'Share link revoked.');
      onChanged();
    } catch (err) {
      if (shareSessionRef.current !== shareSession) return;
      push('error', err instanceof ApiError ? err.message : 'Could not revoke the share link.');
    } finally {
      if (shareSessionRef.current === shareSession) setRevoking(false);
    }
  };

  const revokedAt = optimisticRevokedAt ?? booking.share_revoked_at ?? shareLink?.share_revoked_at;

  return (
    <>
      <Modal open={open} onClose={onClose} title={`Booking #${booking.id}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Row label="User" value={booking.users ? fullName(booking.users) : '—'} />
          <Row label="Phone" value={booking.users?.phone ?? '—'} />
          <Row label="Date" value={booking.date} />
          <Row
            label="Time"
            value={`${formatClock(booking.start_time)} – ${formatClock(booking.end_time)}`}
          />
          <Row label="Duration" value={`${booking.duration_minutes} min`} />
          <Row label="Table" value={booking.tables?.name ?? `#${booking.table_id}`} />
          <Row label="Payment type" value={booking.payment_type} />
          <Row label="Source" value={booking.source} />
          <Row label="Status" value={booking.status} />

          <section
            aria-labelledby="share-link-heading"
            style={{
              marginTop: 8,
              padding: 12,
              background: PS.pageSecondary,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div id="share-link-heading" style={{ ...T.label }}>Share link</div>
            {revokedAt ? (
              <div style={{ ...T.ui, color: PS.textSec }}>
                Revoked at {formatTimestamp(revokedAt)}
              </div>
            ) : !booking.share_enabled_at ? (
              <div style={{ ...T.ui, color: PS.textSec }}>No share link</div>
            ) : shareLoading ? (
              <div style={{ ...T.ui, color: PS.textSec }}>Loading share link…</div>
            ) : shareError ? (
              <div role="alert" style={{ ...T.ui, color: PS.errorText }}>{shareError}</div>
            ) : shareLink ? (
              <>
                {shareLink.url ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      aria-label="Share link URL"
                      readOnly
                      value={shareLink.url}
                      onFocus={(event) => event.currentTarget.select()}
                      style={{
                        minWidth: 0,
                        flex: 1,
                        padding: 8,
                        border: `1px solid ${PS.border}`,
                        borderRadius: 3,
                        background: PS.white,
                        color: PS.nearBlack,
                        ...T.ui,
                      }}
                    />
                    <Button variant="secondary" onClick={() => void handleCopyShareLink()}>
                      Copy
                    </Button>
                  </div>
                ) : (
                  <div style={{ ...T.ui, color: PS.textSec }}>Share URL unavailable</div>
                )}
                <div style={{ ...T.caption, color: PS.textSec }}>
                  Active since {formatTimestamp(shareLink.share_enabled_at)}
                </div>
                <div style={{ ...T.caption, color: PS.textSec }}>
                  Expires {formatTimestamp(shareLink.expires_at)}
                </div>
                <div>
                  <Button
                    variant="danger"
                    onClick={() => setConfirmRevokeOpen(true)}
                    disabled={revoking}
                  >
                    Revoke link
                  </Button>
                </div>
              </>
            ) : null}
          </section>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {booking.status === 'confirmed' && (
              <Button variant="danger" onClick={() => setConfirmCancelOpen(true)}>
                Cancel booking
              </Button>
            )}
          </div>

          {/* Neither block type is reschedulable from here. A coach_block has its
              own "Change times" panel below; a tournament_block is owned by its
              tournament, whose date/times/tables freeze once the first
              participant confirms — moving the block from this modal would
              silently desync the two. */}
          {booking.payment_type !== 'coach_block' &&
            booking.payment_type !== 'tournament_block' &&
            booking.status === 'confirmed' && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: PS.pageSecondary,
                border: `1px solid ${PS.borderLight}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ ...T.label }}>Reschedule</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                {/* Same implicit-association case as the "Change times" panel
                    below: TimeSelect's root is a native <select>, which
                    eslint-plugin-jsx-a11y can't see through. ariaLabel gives
                    the control its own explicit accessible name too. */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={T.label}>New start</span>
                  <TimeSelect
                    value={newStart}
                    onChange={setNewStart}
                    min={POLICY_MIN_TIME}
                    max={rescheduleMaxStart}
                    includeMax
                    stepMinutes={POLICY.SLOT_STEP_MINUTES}
                    ariaLabel="New start time"
                  />
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={T.label}>New end</span>
                  <span style={{ ...T.ui, color: PS.textSec, paddingBottom: 6 }}>
                    {derivedEnd ? formatClock(derivedEnd) : '—'}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => void handleReschedule()}
                  disabled={updating || !rescheduleChanged}
                >
                  {updating ? 'Saving…' : 'Apply'}
                </Button>
              </div>
              <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
                Moves the booking to a new start time on the same day (duration
                kept). Must be in the future, within ±12h, and not clash with
                another booking — the backend enforces this.
              </p>
            </div>
          )}

          {booking.payment_type === 'coach_block' && booking.status === 'confirmed' && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: PS.pageSecondary,
                border: `1px solid ${PS.borderLight}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ ...T.label }}>Change times</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                {/* TimeSelect's root is a native <select>, so this <label> is
                    genuinely (implicitly) associated with it through the
                    component boundary — eslint-plugin-jsx-a11y can't see
                    through custom components to verify that, hence the
                    disable below. ariaLabel gives the control its own
                    explicit accessible name too. */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={T.label}>New start</span>
                  <TimeSelect
                    value={newStart}
                    onChange={setNewStart}
                    min={POLICY_MIN_TIME}
                    max="24:00"
                    stepMinutes={POLICY.SLOT_STEP_MINUTES}
                    ariaLabel="New start time"
                  />
                </label>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={T.label}>New end</span>
                  <TimeSelect
                    value={newEnd}
                    onChange={setNewEnd}
                    min={POLICY_MIN_TIME}
                    max="24:00"
                    includeMax
                    stepMinutes={POLICY.SLOT_STEP_MINUTES}
                    ariaLabel="New end time"
                  />
                </label>
                <Button
                  variant="secondary"
                  onClick={() => void handleUpdateTimes()}
                  disabled={updating || !timesChanged}
                >
                  {updating ? 'Saving…' : 'Apply'}
                </Button>
              </div>
              <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
                New range must stay within the parent series window. The backend
                will reject changes that fall outside it.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmRevokeOpen}
        title="Revoke share link?"
        message="Anyone holding it loses door access immediately."
        confirmLabel="Revoke link"
        cancelLabel="Keep link"
        destructive
        confirmDisabled={revoking}
        onConfirm={handleRevokeShareLink}
        onCancel={() => setConfirmRevokeOpen(false)}
      />

      <ConfirmDialog
        open={confirmCancelOpen}
        title="Cancel booking?"
        message={`Cancel booking #${booking.id} for ${booking.users ? fullName(booking.users) : 'this user'}? This frees the slot.`}
        confirmLabel="Cancel booking"
        cancelLabel="Keep"
        destructive
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancelOpen(false)}
      />
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ ...T.label, width: 120, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack }}>{value}</div>
    </div>
  );
}
